import { useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import type { CreateEmployeeRequest, Guid, PayrollEmployee, UpdateEmployeeRequest } from "@/api/types";
import { DialogShell } from "./DialogShell";
import "./EmployeeFormDialog.css";

/**
 * ef-058-4 — the Add person / Edit dialog the People tab (H2a.tsx) opens.
 *
 * ONE FORM, TWO MODES. `employee` absent = add (name, address, start date, pay basis, rate,
 * H-2A worker → POST /api/demo/employees); present = edit (the same fields minus pay basis
 * and rate, plus Active → PATCH /api/demo/employees/{id}). A rate is never edited here: it is
 * dated history, so it changes only through RateHistoryDialog.
 *
 * VALIDATION IS THE SERVER'S. Only an empty name is stopped client-side; a rate the API
 * rejects is shown from its own 400 (the people-edit spec asserts the round trip). The form
 * stays open for correction and surfaces ONE `role="alert"` (field messages joined) so a
 * screen reader hears a single announcement, with `aria-invalid` on each rejected field.
 *
 * Shaped like W4Dialog: mounts per open, focus trap, Escape/overlay/Cancel close, the caller
 * returns focus to its trigger.
 */

/** A fresh `Idempotency-Key` per dialog open (ef-048-2b convention, see useLivestock.ts). */
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

interface FormState {
  name: string;
  address: string;
  startDate: string;
  payBasis: "hourly" | "salary";
  rate: string;
  ficaExemptByLaw: boolean;
  active: boolean;
}

function initialForm(employee: PayrollEmployee | undefined): FormState {
  if (!employee) {
    return { name: "", address: "", startDate: "", payBasis: "hourly", rate: "", ficaExemptByLaw: false, active: true };
  }
  return {
    name: employee.name,
    address: employee.address ?? "",
    startDate: employee.startDate ?? "",
    payBasis: employee.payBasis === "salary" ? "salary" : "hourly",
    rate: "",
    ficaExemptByLaw: employee.ficaExemptByLaw,
    active: employee.active !== false,
  };
}

/** Field label per server error key (ASP.NET keys arrive in either case). */
const FIELD_LABELS: Record<string, string> = {
  name: "Name",
  address: "Address",
  startdate: "Start date",
  paybasis: "Pay basis",
  rate: "Rate",
  ficaexemptbylaw: "H-2A worker",
  active: "Active",
};

export function EmployeeFormDialog({
  employee,
  entityId,
  onClose,
  onSaved,
}: {
  /** Absent = add a person; present = edit that person. */
  employee?: PayrollEmployee;
  /** The entity a new person joins — the roster's own entity, so they show up on this tab. */
  entityId: Guid | null;
  onClose: () => void;
  /** Called after a successful save, BEFORE `onClose` — the caller refetches the roster. */
  onSaved: () => void;
}) {
  const editing = employee !== undefined;
  const [form, setForm] = useState<FormState>(() => initialForm(employee));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());

  const alertId = useId();
  const ids = {
    name: useId(),
    address: useId(),
    startDate: useId(),
    payBasis: useId(),
    rate: useId(),
    h2a: useId(),
    active: useId(),
  };
  const formId = useId();
  // One key per open. Replaced only after a definite server refusal, never after a lost
  // connection (the create may have landed; the same key lets the server replay it).
  const keyRef = useRef(newIdempotencyKey());
  const inFlight = useRef(false);

  function set<K extends keyof FormState>(field: K, value: FormState[K]): void {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return; // a double-click must not send a second request
    setMessage(null);
    setInvalid(new Set());

    const name = form.name.trim();
    if (name === "") {
      setMessage("Name is required.");
      setInvalid(new Set(["name"]));
      return;
    }

    inFlight.current = true;
    setSaving(true);
    try {
      if (employee) {
        const body: UpdateEmployeeRequest = {
          name,
          address: form.address.trim(),
          ficaExemptByLaw: form.ficaExemptByLaw,
          active: form.active,
          ...(form.startDate !== "" ? { startDate: form.startDate } : {}),
        };
        await demoApi.updateEmployee(employee.id, body);
      } else {
        if (entityId === null) {
          setMessage("Could not tell which business to add this person to.");
          return;
        }
        const body: CreateEmployeeRequest = {
          entityId,
          name,
          address: form.address.trim() === "" ? null : form.address.trim(),
          startDate: form.startDate === "" ? null : form.startDate,
          payBasis: form.payBasis,
          rate: form.rate.trim() === "" ? null : Number(form.rate),
          ficaExemptByLaw: form.ficaExemptByLaw,
        };
        await demoApi.createEmployee(body, keyRef.current);
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) keyRef.current = newIdempotencyKey();
      const fields = problemFieldErrors(err);
      const entries = Object.entries(fields ?? {}).filter(([, msgs]) => msgs.length > 0);
      if (entries.length > 0) {
        setInvalid(new Set(entries.map(([key]) => key.toLowerCase())));
        setMessage(
          entries
            .map(([key, msgs]) => `${FIELD_LABELS[key.toLowerCase()] ?? key}: ${msgs[0] ?? ""}`)
            .join(" "),
        );
      } else {
        setMessage(problemMessage(err, "Could not save this person."));
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  const invalidProps = (key: string) =>
    invalid.has(key) ? { "aria-invalid": true as const, "aria-describedby": alertId } : {};

  return (
    <DialogShell
      title={<>{employee ? `Edit person — ${employee.name}` : "Add person"}</>}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving}>
            {saving ? "Saving…" : "Save person"}
          </button>
        </>
      }
    >
      <form id={formId} className="emp-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="emp-dialog__field">
          <label htmlFor={ids.name}>Name</label>
          <input
            id={ids.name}
            type="text"
            autoComplete="off"
            maxLength={120}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            {...invalidProps("name")}
          />
        </div>

        <div className="emp-dialog__field">
          <label htmlFor={ids.address}>Address</label>
          <input
            id={ids.address}
            type="text"
            autoComplete="off"
            maxLength={300}
            value={form.address}
            onChange={(e) => set("address", e.target.value)}
            {...invalidProps("address")}
          />
        </div>

        <div className="emp-dialog__field">
          <label htmlFor={ids.startDate}>Start date</label>
          <input
            id={ids.startDate}
            type="date"
            value={form.startDate}
            onChange={(e) => set("startDate", e.target.value)}
            {...invalidProps("startdate")}
          />
        </div>

        {!editing && (
          <>
            <div className="emp-dialog__field">
              <label htmlFor={ids.payBasis}>Pay basis</label>
              <select
                id={ids.payBasis}
                value={form.payBasis}
                onChange={(e) => set("payBasis", e.target.value === "salary" ? "salary" : "hourly")}
                {...invalidProps("paybasis")}
              >
                <option value="hourly">Hourly</option>
                <option value="salary">Salary</option>
              </select>
            </div>

            <div className="emp-dialog__field">
              <label htmlFor={ids.rate}>Rate</label>
              <input
                id={ids.rate}
                type="number"
                step="0.01"
                inputMode="decimal"
                value={form.rate}
                onChange={(e) => set("rate", e.target.value)}
                aria-describedby={`${ids.rate}-hint${invalid.has("rate") ? ` ${alertId}` : ""}`}
                aria-invalid={invalid.has("rate") ? true : undefined}
              />
              <p id={`${ids.rate}-hint`} className="emp-dialog__hint">
                {form.payBasis === "hourly" ? "Dollars per hour. Leave blank if not set yet." : "Dollars per pay period."}
              </p>
            </div>
          </>
        )}

        <div className="emp-dialog__field emp-dialog__field--checkbox">
          <input
            id={ids.h2a}
            type="checkbox"
            checked={form.ficaExemptByLaw}
            onChange={(e) => set("ficaExemptByLaw", e.target.checked)}
          />
          <label htmlFor={ids.h2a}>H-2A worker</label>
        </div>

        {editing && (
          <>
            <div className="emp-dialog__field emp-dialog__field--checkbox">
              <input
                id={ids.active}
                type="checkbox"
                checked={form.active}
                onChange={(e) => set("active", e.target.checked)}
              />
              <label htmlFor={ids.active}>Active</label>
            </div>
            <p className="emp-dialog__hint">
              Pay basis and rate change from Rate history. An inactive person stays on People and in
              reports, and is left off the schedule.
            </p>
          </>
        )}

        {message && (
          <p id={alertId} className="emp-dialog__error" role="alert">
            {message}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
